/*
 * JuvaBun GENERIC product page (sections/jb-pdp-gen.liquid, template product.jb). Based on assets/jb-pdp-v3.js.
 * Vanilla JS, deferred. Custom element <jb-pdp-gen>.
 *
 * - Gallery: same as v3 (endless loop: swipe, arrows, dots, "1 / 8"). Never moves on its own. Choosing a shade puts that
 *   shade's photo (+ on-model photos from custom.jb_shade_gallery) first and goes back to slide 1.
 * - Options: shade swatches (data-shade-input) and pill buttons for other options. Each radio carries data-opt=<option
 *   index>; the variant is the one whose options match all choices (or, if that combination does not exist, the first
 *   available variant with the option just chosen). Updates ?variant=, the hidden id input, price, button and fires
 *   "jb:shade-change" (used by Complete the look).
 * - Quantity stepper (1 to maxQty) + Add to cart at the regular price: POST /cart/add.js, then the same events the
 *   theme's product forms fire ("variant:add" and "cart:change") so assets/jb-cart.js opens the cart drawer.
 * - Sticky add to cart (phones) whenever the main button is out of view.
 * - Find my shade: the Messy Bun finder (assets/jb-shade-finder.js, loaded on tap) gets an adapter whose "shades" are
 *   the Messy Bun reference shades; its result is mapped to this product's shades with assets/jb-shade-map.json
 *   (mapResult). Without a map entry: same shade name, or Silver Gray = Light Gray, else "no close match".
 * - "Your match" line under the shade label: the last finder result saved on this device (localStorage
 *   "jb-shade-match", written by jb-shade-finder.js), mapped to this style ("Your match: Silver Gray, in this style:
 *   Light Gray"). Tapping it selects that shade. The map is fetched only when there is a saved match.
 * - Reviews (jb-pdp-reviews-v3) and demo video (jb-pdp-gen-how): same code as v3, below.
 */
(() => {
  if (customElements.get('jb-pdp-gen')) return;

  const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  /* Add to cart goes through jb-cart.js (JBCart.add): serialized with every other cart call, retries network-level
     failures (iOS "Load failed") after checking the add did not already land, so items are never doubled.
     The plain POST below is only a fallback for a page without the cart drawer script. */
  const NET_FAIL_MSG = "We couldn't reach the store. Please check your connection and tap again.";
  const addItems = async (items) => {
    if (window.JBCart && window.JBCart.add) return window.JBCart.add(items);
    let res;
    try {
      res = await fetch(`${ROOT}cart/add.js`, {
        method: 'POST',
        cache: 'no-store',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ items })
      });
    } catch (e) {
      const ne = new Error(NET_FAIL_MSG);
      ne.jbUser = true;
      throw ne;
    }
    const json = await res.json().catch(() => ({}));
    if (!res.ok || json.status) {
      const he = new Error(json.description || json.message || 'Sorry, we could not add this to your cart. Please try again.');
      he.jbUser = true;
      throw he;
    }
    return { items: json.items || items, sections: null, cart: null };
  };
  const cartMessage = (e) => {
    if (window.JBCart && window.JBCart.message) return window.JBCart.message(e);
    return e && e.jbUser ? e.message : 'Sorry, we could not add this to your cart. Please try again.';
  };
  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* Gallery photo sizes: keep in sync with snippets/jb-gal-img.liquid */
  const GAL_WIDTHS = [360, 540, 720, 900, 1080, 1400];
  const GAL_MS = 320; /* endless gallery: slide animation length */
  const GAL_SIZES = '(min-width: 1000px) min(50vw, 600px), (min-resolution: 2.5dppx) 360px, 100vw';
  /* Gallery photos still loading: the beige slide shows (no spinner, same box), then the photo fades in (150ms) */
  if (!document.getElementById('jb-gal-fade-css')) {
    const st = document.createElement('style');
    st.id = 'jb-gal-fade-css';
    st.textContent = '.jb-gal__img.is-loading{opacity:0}.jb-gal__img{transition:opacity .15s ease-out}@media (prefers-reduced-motion:reduce){.jb-gal__img{transition:none}}';
    document.head.appendChild(st);
  }
  /* Run fn once the page has finished loading and the browser is idle (Safari: short timeout instead) */
  const whenIdle = (fn) => {
    const go = () => ('requestIdleCallback' in window ? window.requestIdleCallback(fn, { timeout: 2500 }) : setTimeout(fn, 250));
    if (document.readyState === 'complete') go();
    else window.addEventListener('load', go, { once: true });
  };
  const imgW = (u, w) => String(u || '').replace(/([?&]width=)\d+/, `$1${w}`);
  const galSrcset = (u, max) => {
    const m = Number(max) || 2048;
    const ws = GAL_WIDTHS.filter((w) => w <= m);
    if (m < 1400 && ws.indexOf(m) < 0) ws.push(m);
    return ws.map((w) => `${imgW(u, w)} ${w}w`).join(', ');
  };
  /* The section JSON carries ONE url per photo (width=900): thumb / main / srcset are built here to keep the page small */
  const expandImgs = (d) => {
    ['shades', 'variants', 'refShades'].forEach((k) => (d && Array.isArray(d[k]) ? d[k] : []).forEach((s) => {
      if (s.img) {
        s.thumb = imgW(s.img, 120);
        s.main = s.img;
        s.mainSrcset = galSrcset(s.img);
      }
      /* Hair-texture swatch (snippets/jb-swatch-img): the small round swatches (sticky bar, "your match") use it */
      if (s.sw) s.thumb = s.sw;
      (s.gallery || []).forEach((g) => {
        if (!g.u) g.u = s.img;
        g.src = imgW(g.u, Math.min(900, Number(g.w) || 900));
        g.srcset = galSrcset(g.u, g.w);
      });
    }));
  };
  const lc = (x) => String(x || '').trim().toLowerCase();
  const SAME_COLOR = { 'silver gray': 'light gray', 'light gray': 'silver gray' };

  class JBPdpGen extends HTMLElement {
    connectedCallback() {
      try {
        this.data = JSON.parse(this.querySelector('[data-pdp-json]').textContent);
        expandImgs(this.data);
      } catch (e) {
        return;
      }
      this.variants = this.data.variants || [];
      this.byId = new Map(this.variants.map((v) => [String(v.id), v]));
      this.ci = Number(this.data.colorIndex);
      this.form = this.querySelector('[data-pdp-form]');
      this.idInput = this.querySelector('[data-variant-id]');
      this.variant = this.byId.get(String(this.idInput && this.idInput.value)) || this.variants[0];
      this.initGallery();
      this.initOptions();
      this.initQty();
      this.initAtc();
      this.initSticky();
      this.initFinder();
      this.initShadeMemory();
      this.initTabs();
      this.update();
      window.JBPdp = this; // Complete the look uses JBPdp.money()
      if (this.ci >= 0 && this.variant) {
        document.dispatchEvent(new CustomEvent('jb:shade-change', { detail: { id: this.variant.id, name: this.variant.name, label: this.variant.name, family: this.variant.family, initial: true } }));
      }
    }

    /* ------------------------------------------- info tabs (owner item 16) */
    /* Accordion under the buy area: every tab starts closed, each button opens / closes its own panel */
    initTabs() {
      const btns = Array.from(this.querySelectorAll('[data-tab-btn]'));
      btns.forEach((btn) => {
        const panel = document.getElementById(btn.getAttribute('aria-controls'));
        if (!panel) return;
        btn.addEventListener('click', () => {
          const open = btn.getAttribute('aria-expanded') !== 'true';
          btn.setAttribute('aria-expanded', String(open));
          panel.hidden = !open;
        });
      });
      // Theme editor: selecting an "Info tab" block opens it
      if (btns.length && window.Shopify && window.Shopify.designMode) {
        document.addEventListener('shopify:block:select', (e) => {
          const b = e.target && e.target.querySelector ? e.target.querySelector('[data-tab-btn]') : null;
          if (b && this.contains(b) && b.getAttribute('aria-expanded') !== 'true') b.click();
        });
      }
    }

    $(sel) {
      return this.querySelector(sel) || (this.sticky ? this.sticky.querySelector(sel) : null);
    }

    /* ------------------------------------------------------------ money */
    money(cents) {
      const fmt = this.data.moneyFormat || '${{amount}}';
      const n = Math.round(Number(cents) || 0);
      const withDelims = (num, dec, th, ds) => {
        const parts = (num / 100).toFixed(dec).split('.');
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, th);
        return parts.join(ds);
      };
      return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (m, key) => {
        switch (key) {
          case 'amount_no_decimals': return withDelims(n, 0, ',', '.');
          case 'amount_with_comma_separator': return withDelims(n, 2, '.', ',');
          case 'amount_no_decimals_with_comma_separator': return withDelims(n, 0, '.', ',');
          default: return withDelims(n, 2, ',', '.');
        }
      }).replace(/<[^>]*>/g, '');
    }

    /* ---------------------------------------------------------- gallery */
    /* ENDLESS LOOP (owner item 12): the slides stay in one flex row, but each one is placed with a transform, so only
       the current photo and its two neighbours sit next to the frame, wrapping around (the last photo sits left of the
       first one, the first right of the last). Swipe (pointer events; vertical page scroll stays native through
       touch-action: pan-y), arrows, dots, arrow keys and a sideways trackpad swipe all move one photo, and after the
       last photo comes the first with no rewind. Dots and "1 / 8" always show the real photo. No JS: the track stays a
       native scroll-snap row. prefers-reduced-motion: photos change without the slide animation. */
    initGallery() {
      const g = this.querySelector('[data-gallery]');
      // v43 guard (Clarity W44: "Cannot read properties of null (reading 'addEventListener')" on /products/bobby-pins):
      // a gallery without its track (one-photo products, editor changes) leaves the page working without the slider
      if (!g || !g.querySelector('[data-gal-track]')) return;
      this.gallery = g;
      this.track = g.querySelector('[data-gal-track]');
      this.dotsWrap = g.querySelector('[data-gal-dots]');
      this.count = g.querySelector('[data-gal-count]');
      this.prevBtn = g.querySelector('[data-gal-prev]');
      this.nextBtn = g.querySelector('[data-gal-next]');
      this.slideIndex = 0;
      if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.goTo(this.slideIndex - 1, false, -1));
      if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.goTo(this.slideIndex + 1, false, 1));
      if (this.dotsWrap) {
        this.dotsWrap.addEventListener('click', (e) => {
          const d = e.target.closest('[data-gal-dot]');
          if (d) this.goTo(Number(d.dataset.galDot));
        });
      }
      this.track.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') { e.preventDefault(); this.goTo(this.slideIndex + 1, false, 1); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); this.goTo(this.slideIndex - 1, false, -1); }
      });
      // A swipe made before this script ran (native scroll row): start from that photo
      const start = Math.round(this.track.scrollLeft / Math.max(1, this.track.clientWidth)) || 0;
      this.track.scrollLeft = 0;
      this.track.classList.add('is-loop');
      // The row never scrolls in loop mode (focus inside a slide could otherwise scroll a clipped box)
      this.track.addEventListener('scroll', () => { if (this.track.scrollLeft) this.track.scrollLeft = 0; }, { passive: true });
      this.refreshSlides();
      this.initGalDrag();
      this.initGalVideos();
      this.slideIndex = Math.max(0, Math.min(this.slides.length - 1, start));
      this.placeSlides(this.slideIndex, {});
      this.setSlide(this.slideIndex);
      this.primeShade(true);
      ['pointerdown', 'touchstart', 'keydown', 'focusin'].forEach((ev) => g.addEventListener(ev, () => { if (!this.galWarm) this.warmGallery(); }, { passive: true }));
    }

    galWrap(i) {
      const n = this.slides ? this.slides.length : 0;
      return n ? ((i % n) + n) % n : 0;
    }

    /* Where slide k sits for the photo `center`: 0 = in the frame, -1 / 1 = just left / right of it (wrapping around),
       anything else = parked out of sight. With 2 photos the other one waits on the `side` it will come from. */
    galRel(k, center, side) {
      const n = this.slides.length;
      let d = (((k - center) % n) + n) % n;
      if (d > n / 2) d -= n;
      if (n === 2 && d !== 0) d = side < 0 ? -1 : 1;
      return d;
    }

    /* opts: side (2 photos: where the other one waits), offset (px, finger drag), over ({ slideIndex: position }),
       animate (slide only the photos that stay next to the frame; parked ones jump unseen) */
    placeSlides(center, opts) {
      if (!this.slides) return;
      const o = opts || {};
      const off = Math.round(o.offset || 0);
      const anim = !!o.animate && !reduceMotion();
      this.slides.forEach((s, k) => {
        let p = o.over && o.over[k] !== undefined ? o.over[k] : this.galRel(k, center, o.side || 1);
        const near = Math.abs(p) <= 1;
        if (!near) p = p < 0 ? -2 : 2;
        const was = s._jbP;
        const glide = anim && near && was !== undefined && Math.abs(was) <= 1 && Math.abs(was - p) <= 1;
        s.style.transition = glide ? `transform ${GAL_MS}ms cubic-bezier(.2, .7, .2, 1)` : 'none';
        s.style.transform = `translateX(calc(${(p - k) * 100}% + ${near ? off : 0}px))`;
        s.style.visibility = near ? '' : 'hidden';
        s._jbP = p;
      });
    }

    /* Finger / mouse drag. Horizontal moves drag the photos, vertical ones are left to the page scroll. */
    initGalDrag() {
      const t = this.track;
      // Listen on the whole gallery so a swipe that starts on an arrow button still moves the photos
      const g = this.gallery || t;
      let st = null;
      t.addEventListener('dragstart', (e) => e.preventDefault());
      g.addEventListener('pointerdown', (e) => {
        if (!this.slides || this.slides.length < 2 || !e.isPrimary || e.button > 0) return;
        if (e.target.closest && e.target.closest('a, input, video[controls], [data-gal-dots], .jb-gal__bar')) return;
        st = { id: e.pointerId, x: e.clientX, y: e.clientY, dx: 0, on: false, pts: [[e.timeStamp, e.clientX]] };
      });
      g.addEventListener('pointermove', (e) => {
        if (!st || e.pointerId !== st.id) return;
        const dx = e.clientX - st.x;
        const dy = e.clientY - st.y;
        if (!st.on) {
          if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
          if (Math.abs(dy) >= Math.abs(dx)) { st = null; return; } // the page is being scrolled
          st.on = true;
          this.galDragging = true;
          clearTimeout(this.galT);
          try { t.setPointerCapture(e.pointerId); } catch (err) { /* ignore */ }
          t.classList.add('is-dragging');
          this.galWarm = true;
          this.loadNeighbours();
        }
        if (e.cancelable) e.preventDefault();
        const w = t.clientWidth || 1;
        st.dx = Math.max(-w, Math.min(w, dx));
        st.pts.push([e.timeStamp, e.clientX]);
        if (st.pts.length > 5) st.pts.shift();
        this.placeSlides(this.slideIndex, { side: st.dx < 0 ? 1 : -1, offset: st.dx });
      });
      const end = (e) => {
        if (!st || e.pointerId !== st.id) return;
        const s = st;
        st = null;
        if (!s.on) return;
        t.classList.remove('is-dragging');
        this.galJustDragged = true;
        setTimeout(() => { this.galJustDragged = false; }, 80);
        const w = t.clientWidth || 1;
        const a = s.pts[0];
        const b = s.pts[s.pts.length - 1];
        const v = (b[1] - a[1]) / Math.max(1, b[0] - a[0]); // px per ms over the last moves
        const dir = s.dx < 0 ? 1 : -1;
        const flick = Math.abs(v) > 0.3 && Math.abs(s.dx) > 24 && Math.sign(v) === Math.sign(s.dx);
        // A pointercancel mid-swipe (iOS hands the gesture to the browser) still counts if the photo moved far enough
        if (s.dx !== 0 && (Math.abs(s.dx) > w * 0.2 || flick)) {
          this.goTo(this.slideIndex + dir, false, dir);
        } else {
          this.placeSlides(this.slideIndex, { side: dir, animate: true });
          clearTimeout(this.galT);
          this.galT = setTimeout(() => this.placeSlides(this.slideIndex, {}), GAL_MS + 60);
        }
        this.galDragging = false;
      };
      g.addEventListener('pointerup', end);
      g.addEventListener('pointercancel', end);
      // iOS Safari: pointer events cannot stop the browser's own sideways pan, which then cancels the swipe (it only
      // happened swiping to the NEXT photo). Once a touch moves mostly sideways, block the native pan here.
      let tx = 0;
      let ty = 0;
      let tdir = 0;
      g.addEventListener('touchstart', (e) => {
        const p = e.touches[0];
        tx = p.clientX; ty = p.clientY; tdir = 0;
      }, { passive: true });
      g.addEventListener('touchmove', (e) => {
        if (!this.slides || this.slides.length < 2 || e.touches.length > 1) return;
        const p = e.touches[0];
        const dx = p.clientX - tx;
        const dy = p.clientY - ty;
        if (!tdir && (Math.abs(dx) > 4 || Math.abs(dy) > 4)) tdir = Math.abs(dx) > Math.abs(dy) ? 1 : 2;
        if (tdir === 1 && e.cancelable) e.preventDefault();
      }, { passive: false });
      // A drag is not a tap on whatever is under the finger
      g.addEventListener('click', (e) => { if (this.galJustDragged) { e.preventDefault(); e.stopPropagation(); } }, true);
      // Sideways trackpad swipe (desktop): one photo per gesture
      let acc = 0;
      let lock = false;
      let wt = 0;
      t.addEventListener('wheel', (e) => {
        if (!this.slides || this.slides.length < 2 || Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;
        e.preventDefault();
        clearTimeout(wt);
        wt = setTimeout(() => { lock = false; acc = 0; }, 180);
        if (lock) return;
        acc += e.deltaX;
        if (Math.abs(acc) > 30) {
          const dir = acc > 0 ? 1 : -1;
          lock = true;
          acc = 0;
          this.goTo(this.slideIndex + dir, false, dir);
        }
      }, { passive: false });
    }

    refreshSlides() {
      this.slides = Array.from(this.track.querySelectorAll('[data-gal-slide]'));
      const n = this.slides.length;
      this.slides.forEach((s, k) => s.setAttribute('aria-label', `${k + 1} of ${n}`));
      if (this.dotsWrap && this.dotsWrap.children.length !== n) {
        this.dotsWrap.innerHTML = this.slides.map((s, k) => `<button type="button" class="jb-gal__dot" data-gal-dot="${k}" aria-label="Show photo ${k + 1}"></button>`).join('');
      }
      this.dots = this.dotsWrap ? Array.from(this.dotsWrap.querySelectorAll('[data-gal-dot]')) : [];
    }

    /* Put the chosen variant's photos first (data in the section JSON, no fetch) */
    showVariantGallery(v) {
      if (!this.track || !v || !Array.isArray(v.gallery) || !v.gallery.length) return;
      const old = Array.from(this.track.querySelectorAll('[data-gal-shade]'));
      const esc = (t) => String(t || '').replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
      const html = v.gallery.map((g, k) => {
        const alt = g.alt || `${this.data.productTitle} in ${v.name}`;
        return `<figure class="jb-gal__slide${g.model ? ' jb-gal__slide--model' : ''}" data-gal-slide data-gal-shade data-fit="${g.model ? 'cover-top' : 'contain'}" role="group" aria-roledescription="slide">`
          + (k === 0
            ? `<img class="jb-gal__img jb-gal__img--main" src="${esc(g.src)}" srcset="${esc(g.srcset)}" sizes="${GAL_SIZES}" width="${g.w}" height="${g.h}" alt="${esc(alt)}" fetchpriority="high"></figure>`
            : `<img class="jb-gal__img" data-src="${esc(g.src)}" data-srcset="${esc(g.srcset)}" sizes="${GAL_SIZES}" width="${g.w}" height="${g.h}" alt="${esc(alt)}" loading="lazy" decoding="async"></figure>`);
      }).join('');
      const tmp = document.createElement('div');
      tmp.innerHTML = html;
      const first = old[0] || this.track.firstElementChild;
      Array.from(tmp.children).forEach((el) => this.track.insertBefore(el, first));
      old.forEach((el) => el.remove());
      this.refreshSlides();
      this.galWarm = false; // a new shade: its first photo now (high), the next 2 right away, the rest when idle
      this.primeShade(false);
      this.goTo(0, true);
    }

    /* Show photo i (wraps around). dir: 1 = the photo comes in from the right, -1 = from the left
       (arrows, swipe and keys pass it; dots: forward when the photo is later in the row) */
    goTo(i, instant, dir) {
      if (!this.track || !this.slides || !this.slides.length) return;
      if (!instant) this.galWarm = true;
      const from = this.slideIndex;
      const to = this.galWrap(i);
      const d = dir || (to >= from ? 1 : -1);
      clearTimeout(this.galT);
      if (!instant && !reduceMotion() && to !== from && this.slides.length > 1) {
        if (!this.galDragging) {
          // 1. the photo waits right next to the frame, on the side it comes from (no animation)
          const pre = {};
          pre[to] = d;
          this.placeSlides(from, { side: d, over: pre });
          void this.track.offsetWidth; // commit that position before the slide starts
        }
        // 2. the current photo slides out, the new one slides in
        const fin = {};
        fin[from] = -d;
        this.placeSlides(to, { side: -d, over: fin, animate: true });
        this.galT = setTimeout(() => this.placeSlides(this.slideIndex, {}), GAL_MS + 60);
      } else {
        this.placeSlides(to, {});
      }
      this.setSlide(to);
    }

    /* Photos after the first carry data-src: load slide i when shown, and the next one once the shopper interacts */
    /* The slide's own <img> gets its srcset + sizes, so the browser picks one candidate and downloads it once
       (no separate warm-up request at another width). prio: 'high' when shown, 'low' when warming ahead. */
    loadSlide(i, prio) {
      const s = this.slides && this.slides[i];
      const gv = s && s.querySelector('video[data-jb-galvid]');
      if (gv) { if (gv.dataset.poster) { gv.poster = gv.dataset.poster; gv.removeAttribute('data-poster'); } return; }
      const img = s && s.querySelector('img.jb-gal__img');
      if (!img) return;
      if (!img.hasAttribute('data-src')) {
        if (prio === 'high' && !img.complete) img.setAttribute('fetchpriority', 'high');
        return;
      }
      img.loading = 'eager';
      img.setAttribute('fetchpriority', prio || 'auto');
      img.classList.add('is-loading');
      const done = () => img.classList.remove('is-loading');
      img.addEventListener('load', done, { once: true });
      img.addEventListener('error', done, { once: true });
      if (img.dataset.srcset) img.srcset = img.dataset.srcset;
      img.src = img.dataset.src;
      img.removeAttribute('data-srcset');
      img.removeAttribute('data-src');
      if (img.complete && img.naturalWidth) done();
    }

    /* Warm the current shade's photos so swiping never waits:
       first load: slides 2-3 right after the first photo (LCP) has loaded, the rest once the page is idle;
       shade change: the new first photo loads now (high), slides 2-3 right away (low), the rest when idle. */
    primeShade(initial) {
      if (!this.slides || !this.slides.length) return;
      const tok = (this.primeTok = (this.primeTok || 0) + 1);
      const live = () => tok === this.primeTok;
      const idx = [];
      this.slides.forEach((s, k) => { if (s.hasAttribute('data-gal-shade')) idx.push(k); });
      if (!idx.length) return;
      const first = this.slides[idx[0]].querySelector('img');
      const afterFirst = (fn) => {
        if (!first || (first.complete && first.naturalWidth)) { fn(); return; }
        first.addEventListener('load', fn, { once: true });
        first.addEventListener('error', fn, { once: true });
      };
      const next2 = () => { if (live()) idx.slice(1, 3).forEach((k) => this.loadSlide(k, 'low')); };
      const rest = () => whenIdle(() => { if (live()) idx.slice(3).forEach((k) => this.loadSlide(k, 'low')); });
      if (initial) afterFirst(() => { next2(); rest(); });
      else { next2(); afterFirst(rest); }
    }

    warmGallery() {
      this.galWarm = true;
      this.loadSlide(this.slideIndex);
      this.loadNeighbours();
    }

    /* Both photos next to the current one (wrapping: from photo 1, a swipe back shows the last photo) */
    loadNeighbours() {
      if (!this.slides || this.slides.length < 2) return;
      this.loadSlide(this.galWrap(this.slideIndex + 1));
      this.loadSlide(this.galWrap(this.slideIndex - 1));
    }

    setSlide(i) {
      this.slideIndex = i;
      const n = this.slides.length;
      if (this.count) this.count.textContent = `${i + 1} / ${n}`;
      this.dots.forEach((d, k) => (k === i ? d.setAttribute('aria-current', 'true') : d.removeAttribute('aria-current')));
      // Endless loop: the arrows always work (only a single photo has nowhere to go)
      if (this.prevBtn) this.prevBtn.disabled = n < 2;
      if (this.nextBtn) this.nextBtn.disabled = n < 2;
      if (this.galWarm) { this.loadSlide(i, 'high'); this.loadNeighbours(); }
      this.syncGalVideo();
    }

    /* Product videos in the gallery (sections: video media listed in gallery_positions): muted, looping, inline like a
       GIF. Nothing downloads until the video slide is the active slide (or the next one: poster only). It plays while it
       is the active slide and the gallery is at least half on screen, and pauses otherwise.
       prefers-reduced-motion: no autoplay, a "Play video" button starts it with controls. */
    initGalVideos() {
      this.galVids = Array.from(this.track.querySelectorAll('video[data-jb-galvid]'));
      if (!this.galVids.length) return;
      this.galVids.forEach((v) => {
        v.muted = true;
        const btn = v.parentElement.querySelector('[data-video-play]');
        if (!btn) return;
        btn.hidden = !reduceMotion();
        btn.addEventListener('click', () => {
          v._jbUser = true;
          v.controls = true;
          btn.hidden = true;
          this.galVidLoad(v);
          v.play().catch(() => { btn.hidden = false; });
        });
      });
      this.galInView = !('IntersectionObserver' in window);
      if (!this.galInView) {
        new IntersectionObserver((es) => es.forEach((e) => { this.galInView = e.intersectionRatio >= 0.5; this.syncGalVideo(); }), { threshold: [0, 0.5] }).observe(this.gallery);
      }
    }

    galVidLoad(v) {
      if (v._jbLoaded) return;
      v._jbLoaded = true;
      if (v.dataset.poster) { v.poster = v.dataset.poster; v.removeAttribute('data-poster'); }
      v.querySelectorAll('source[data-src]').forEach((so) => { so.src = so.dataset.src; so.removeAttribute('data-src'); });
      v.preload = 'auto';
      v.load();
    }

    syncGalVideo() {
      if (!this.galVids || !this.galVids.length || !this.slides) return;
      const cur = this.slides[this.slideIndex];
      this.galVids.forEach((v) => {
        const on = !!(this.galInView && cur && cur.contains(v));
        if (on && (!reduceMotion() || v._jbUser)) {
          this.galVidLoad(v);
          const p = v.play();
          if (p && p.catch) p.catch(() => {});
        } else if (!on && !v.paused) {
          v.pause();
        }
      });
    }

    /* ---------------------------------------------------------- options */
    initOptions() {
      this.optInputs = Array.from(this.querySelectorAll('input[data-opt]'));
      this.optInputs.forEach((input) => input.addEventListener('change', () => {
        if (input.checked) this.onOptionChange(Number(input.dataset.opt), input.value);
      }));
    }

    chosen() {
      const opts = (this.variant && this.variant.options ? this.variant.options : []).slice();
      this.optInputs.forEach((i) => { if (i.checked) opts[Number(i.dataset.opt)] = i.value; });
      return opts;
    }

    onOptionChange(idx, value) {
      const want = this.chosen();
      want[idx] = value;
      const real = this.variants.filter((v) => !v.placeholder);
      let v = real.find((x) => x.options.every((o, k) => o === want[k]));
      if (!v) v = real.find((x) => x.options[idx] === value && x.available) || real.find((x) => x.options[idx] === value);
      if (v) this.setVariant(v, { showImage: idx === this.ci || this.ci < 0 });
    }

    /* Select a variant (by object or id): radios, hidden id, gallery, URL, price, events */
    setVariant(v, opts = {}) {
      if (typeof v !== 'object') v = this.byId.get(String(v));
      if (!v) return;
      this.variant = v;
      if (this.idInput) this.idInput.value = v.id;
      this.optInputs.forEach((i) => { i.checked = v.options[Number(i.dataset.opt)] === i.value; });
      const name = this.querySelector('[data-shade-name]');
      if (name && this.ci >= 0) name.textContent = v.name;
      this.querySelectorAll('[data-opt-name]').forEach((el) => { el.textContent = v.options[Number(el.dataset.optName)] || ''; });
      const bs = this.querySelector('[data-shade-bs]');
      if (bs) bs.hidden = !v.bestseller;
      if (opts.showImage !== false) this.showVariantGallery(v);
      try {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', v.id);
        window.history.replaceState(window.history.state, '', url.toString());
      } catch (e) { /* ignore */ }
      this.update();
      this.renderMemLine();
      if (this.ci >= 0) document.dispatchEvent(new CustomEvent('jb:shade-change', { detail: { id: v.id, name: v.name, label: v.name, family: v.family } }));
    }

    /* -------------------------------------------------------- quantity */
    initQty() {
      this.qtyInput = this.querySelector('[data-qty]');
      this.minus = this.querySelector('[data-qty-minus]');
      this.plus = this.querySelector('[data-qty-plus]');
      if (!this.qtyInput) return;
      if (this.minus) this.minus.addEventListener('click', () => this.setQty(this.qty() - 1));
      if (this.plus) this.plus.addEventListener('click', () => this.setQty(this.qty() + 1));
      this.qtyInput.addEventListener('change', () => this.setQty(this.qty()));
      this.qtyInput.addEventListener('input', () => this.update());
    }

    qty() {
      const max = Number(this.data.maxQty) || 10;
      const n = parseInt(this.qtyInput ? this.qtyInput.value : '1', 10);
      return Math.max(1, Math.min(max, Number.isFinite(n) ? n : 1));
    }

    setQty(n) {
      const max = Number(this.data.maxQty) || 10;
      if (this.qtyInput) this.qtyInput.value = String(Math.max(1, Math.min(max, n)));
      this.update();
    }

    update() {
      const v = this.variant;
      if (!v) return;
      const q = this.qty();
      const totalText = this.money(v.price * q);
      this.querySelectorAll('[data-unit-price]').forEach((el) => { el.textContent = this.money(v.price); });
      const t = this.querySelector('[data-atc-total]');
      if (t) t.textContent = totalText;
      const max = Number(this.data.maxQty) || 10;
      if (this.minus) this.minus.disabled = q <= 1;
      if (this.plus) this.plus.disabled = q >= max;
      const addText = v.available ? (q > 1 ? `Add ${q} to cart` : 'Add to cart') : 'Sold out';
      const al = this.querySelector('[data-atc-label]');
      if (al && !this.adding) al.textContent = addText;
      const sl = this.$('[data-sticky-label]');
      if (sl) sl.textContent = addText;
      const sw = this.$('[data-sticky-swatch]');
      if (sw && v.thumb) sw.style.backgroundImage = `url("${v.thumb}")`;
      const sp = this.$('[data-sticky-price]');
      if (sp) sp.textContent = totalText;
      const atc = this.querySelector('[data-atc]');
      if (atc && !this.adding) atc.disabled = !v.available;
      const sb = this.$('[data-sticky-btn]');
      if (sb && !this.adding) {
        sb.disabled = !v.available;
        sb.setAttribute('aria-label', `${addText}${this.ci >= 0 ? `, ${v.name}` : ''}, ${totalText}`);
      }
    }

    /* ---------------------------------------------------- add to cart */
    initAtc() {
      if (!this.form) return;
      this.form.addEventListener('submit', (e) => {
        e.preventDefault();
        this.addToCart();
      });
      const stickyBtn = this.querySelector('[data-sticky-btn]');
      if (stickyBtn) stickyBtn.addEventListener('click', () => this.addToCart());
    }

    async addToCart() {
      if (this.adding || !this.variant || !this.variant.available) return;
      const items = [{ id: this.variant.id, quantity: this.qty() }];
      const btns = [this.querySelector('[data-atc]'), this.$('[data-sticky-btn]')].filter(Boolean);
      const err = this.querySelector('[data-atc-error]');
      if (err) { err.hidden = true; err.textContent = ''; }
      this.adding = true;
      btns.forEach((b) => { b.disabled = true; b.setAttribute('aria-busy', 'true'); b.classList.add('is-loading'); });
      const label = this.querySelector('[data-atc-label]');
      if (label) label.textContent = 'Adding';
      try {
        const r = await addItems(items);
        const cart = Object.assign({}, r.cart || {}, { sections: r.sections });
        document.dispatchEvent(new CustomEvent('variant:add', { bubbles: true, detail: { items: r.items, cart } }));
        document.documentElement.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'variant:add', cart, onSuccessDo: null } }));
        this.dispatchEvent(new CustomEvent('jb:added', { bubbles: true, detail: { items, qty: items[0].quantity } }));
      } catch (e) {
        if (err) { err.textContent = cartMessage(e); err.hidden = false; }
      } finally {
        this.adding = false;
        btns.forEach((b) => { b.removeAttribute('aria-busy'); b.classList.remove('is-loading'); });
        this.update();
      }
    }

    /* ---------------------------------------------------------- sticky */
    initSticky() {
      this.sticky = this.querySelector('[data-sticky]');
      const target = this.querySelector('[data-atc]');
      if (!this.sticky || !target) return;
      document.body.appendChild(this.sticky);
      document.documentElement.classList.add('jb-has-sticky');
      this.stickyPast = false;
      if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver((entries) => {
          entries.forEach((e) => {
            this.stickyPast = !e.isIntersecting;
            this.toggleSticky(this.stickyPast);
          });
        }, { threshold: 0 });
        io.observe(target);
      } else {
        this.stickyPast = true;
        this.toggleSticky(true);
      }
    }

    toggleSticky(show) {
      if (!this.sticky) return;
      const on = show && !(this.finder && this.finder.open);
      this.sticky.classList.toggle('is-on', on);
      this.sticky.setAttribute('aria-hidden', on ? 'false' : 'true');
      if (on) this.sticky.removeAttribute('inert'); else this.sticky.setAttribute('inert', '');
      document.documentElement.classList.toggle('jb-sticky-on', on);
    }

    /* ------------------------------------------- shade map (Messy Bun shade -> this product's shade) */
    loadMap() {
      if (!this.data.mapUrl) return Promise.resolve(null);
      if (!this._mapP) {
        this._mapP = fetch(this.data.mapUrl, { credentials: 'same-origin' })
          .then((r) => (r.ok ? r.json() : null))
          .then((j) => { this.map = j; return j; })
          .catch(() => null);
      }
      return this._mapP;
    }

    /* This product's best variant for a shade name (keeps the other chosen options when possible) */
    variantForShade(name) {
      if (!name || this.ci < 0) return null;
      const cur = this.chosen();
      const same = this.variants.filter((v) => !v.placeholder && lc(v.name) === lc(name));
      return same.find((v) => v.available && v.options.every((o, k) => k === this.ci || o === cur[k]))
        || same.find((v) => v.available) || same[0] || null;
    }

    asShade(v) {
      return v ? { id: v.id, name: v.name, label: v.name, thumb: v.thumb, main: v.main, mainSrcset: v.mainSrcset, available: v.available } : null;
    }

    /* Candidates for one Messy Bun shade: [names...] + confidence. Map first, then same name / same color. */
    candidatesFor(refName) {
      // products: handle -> palette id (styles with the same shade list share one palette)
      let entry = this.map && this.map.products && this.map.products[this.data.handle];
      if (typeof entry === 'string') entry = this.map.palettes && this.map.palettes[entry];
      const row = entry && (entry[refName] || entry[Object.keys(entry).find((k) => lc(k) === lc(refName))]);
      if (row) return { names: [row[0], row[1]].filter(Boolean), conf: row[2] || 'low' };
      if (this.variantForShade(refName)) return { names: [refName], conf: 'exact' };
      const twin = SAME_COLOR[lc(refName)];
      if (twin && this.variantForShade(twin)) return { names: [twin], conf: 'same' };
      return { names: [], conf: 'none' };
    }

    /* Called by the finder (result + memory views) */
    mapResult(best, other) {
      if (!best) return { best: null, other: null, conf: 'none' };
      const c = this.candidatesFor(best.name);
      const found = c.names.map((n) => this.variantForShade(n)).filter(Boolean);
      const b = found.find((v) => v.available) || found[0] || null;
      let o = null;
      if (other) {
        const c2 = this.candidatesFor(other.name);
        o = c2.names.map((n) => this.variantForShade(n)).find((v) => v && v.available && (!b || v.id !== b.id)) || null;
      }
      if (!o) o = found.find((v) => v.available && (!b || v.id !== b.id)) || null;
      return { best: this.asShade(b), other: this.asShade(o), conf: b ? c.conf : 'none' };
    }

    /* ---------------------------------------------------------- finder */
    initFinder() {
      this.finder = this.querySelector('[data-finder]');
      if (!this.finder) return;
      const self = this;
      // What the finder sees: Messy Bun shades as "shades", results mapped to this product
      this.finderAdapter = {
        data: {
          shades: this.data.refShades || [],
          profilesUrl: this.data.profilesUrl,
          productTitle: this.data.productTitle,
          exchangeLabel: this.data.exchangeLabel,
          bundles: {}
        },
        money: (c) => self.money(c),
        mapResult: (b, o) => self.mapResult(b, o),
        applyFinderResult: (id) => self.applyFinderResult(id),
        closeFinder: () => self.closeFinder()
      };
      this.querySelectorAll('[data-open-finder]').forEach((b) => b.addEventListener('click', () => this.openFinder(b)));
      document.addEventListener('click', (e) => {
        const a = e.target.closest('a[href$="#find-my-shade"]');
        if (!a) return;
        e.preventDefault();
        this.openFinder(a);
      });
      if (window.location.hash === '#find-my-shade') setTimeout(() => this.openFinder(), 300);
    }

    openFinder(opener) {
      this.finderOpener = opener || null;
      if (!this.finder.open) {
        if (typeof this.finder.showModal === 'function') this.finder.showModal(); else this.finder.setAttribute('open', '');
      }
      document.documentElement.classList.add('jb-sf-lock');
      this.toggleSticky(false);
      if (!this.finder._wired) {
        this.finder._wired = true;
        const sfClose = this.finder.querySelector('[data-sf-close]');
        if (sfClose) sfClose.addEventListener('click', () => this.closeFinder());
        this.finder.addEventListener('cancel', (e) => { e.preventDefault(); this.closeFinder(); });
        this.finder.addEventListener('close', () => {
          document.documentElement.classList.remove('jb-sf-lock');
          this.toggleSticky(!!this.stickyPast);
        });
      }
      Promise.all([this.loadFinder(), this.loadMap()]).then(() => {
        if (window.JBShadeFinder) window.JBShadeFinder.attach(this.finder, this.finderAdapter);
      });
    }

    closeFinder() {
      if (this.finder.open) {
        if (typeof this.finder.close === 'function') this.finder.close(); else this.finder.removeAttribute('open');
      }
      document.documentElement.classList.remove('jb-sf-lock');
      if (this.finderOpener && document.contains(this.finderOpener)) this.finderOpener.focus();
    }

    loadFinder() {
      if (window.JBShadeFinder) return Promise.resolve();
      if (this._finderLoading) return this._finderLoading;
      this._finderLoading = new Promise((resolve) => {
        const s = document.createElement('script');
        s.src = this.dataset.finderSrc;
        s.async = true;
        s.onload = () => resolve();
        s.onerror = () => resolve();
        document.head.appendChild(s);
      });
      return this._finderLoading;
    }

    /* The finder passes THIS product's variant id (already mapped) */
    applyFinderResult(id) {
      const v = this.byId.get(String(id));
      if (v && v.available) this.setVariant(v);
      this.closeFinder();
      const target = this.querySelector('#jb-shades');
      if (target) {
        target.scrollIntoView({ behavior: reduceMotion() ? 'auto' : 'smooth', block: 'start' });
        const focusEl = this.optInputs.find((i) => i.checked && i.dataset.opt === String(this.ci));
        if (focusEl) setTimeout(() => focusEl.focus({ preventScroll: true }), 400);
      }
    }

    /* ------------------------------------------- "Your match" (saved by the shade finder on this device) */
    initShadeMemory() {
      const top = this.querySelector('.jb-shades__top');
      if (!top || !this.finder) return;
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'jb-mymatch';
      b.hidden = true;
      b.innerHTML = '<span class="jb-mymatch__sw" aria-hidden="true"></span><span class="jb-mymatch__txt">Your match: <strong data-mm-ref></strong><span class="jb-mymatch__map" data-mm-map></span></span><span class="jb-mymatch__act" aria-hidden="true"></span>';
      top.after(b);
      this.memLine = b;
      b.addEventListener('click', () => { if (this.memTarget) this.setVariant(this.memTarget); });
      document.addEventListener('jb:shade-memory', () => this.renderMemLine());
      if (this.readShadeMemory()) this.loadMap().then(() => this.renderMemLine());
    }

    readShadeMemory() {
      try {
        const o = JSON.parse(window.localStorage.getItem('jb-shade-match') || 'null');
        const it = o && Array.isArray(o.items) ? o.items[0] : null;
        return it && Array.isArray(it.shades) && it.shades[0] && it.shades[0].name ? it : null;
      } catch (e) {
        return null;
      }
    }

    renderMemLine() {
      const b = this.memLine;
      if (!b) return;
      const it = this.readShadeMemory();
      if (it && !this.map && this.data.mapUrl && !this._mapP) { this.loadMap().then(() => this.renderMemLine()); return; }
      const ref = it ? it.shades[0] : null;
      const m = ref ? this.mapResult({ name: ref.name }, null) : null;
      const v = m && m.best ? this.byId.get(String(m.best.id)) : null;
      this.memTarget = v && v.available ? v : null;
      if (!this.memTarget) { b.hidden = true; return; }
      const on = this.variant && String(this.variant.id) === String(v.id);
      const refName = String(ref.label || ref.name).split(' (also called ')[0];
      const differs = lc(refName) !== lc(v.name);
      b.hidden = false;
      b.classList.toggle('is-on', on);
      b.querySelector('[data-mm-ref]').textContent = refName;
      b.querySelector('[data-mm-map]').textContent = differs ? `In this style: ${v.name}` : '';
      b.querySelector('.jb-mymatch__act').textContent = on ? 'Selected' : 'Select';
      b.querySelector('.jb-mymatch__sw').style.backgroundImage = v.thumb ? `url("${v.thumb}")` : '';
      b.setAttribute('aria-label', `Your saved shade match: ${refName}${differs ? `, in this style ${v.name}` : ''}. ${on ? 'Selected' : 'Select it'}`);
    }
  }

  customElements.define('jb-pdp-gen', JBPdpGen);

  /* ------------------------------------------------ reviews (jb-pdp-reviews-v3): topic chips + "Show more"
     Order of what "Show more reviews" reveals (one batch per tap, filtered by the topic chip):
     1. Live photo and video reviews from the Loox feed (rendered by Liquid, about 40).
     2. Older photo reviews from the owner's Loox CSV export: assets/jb-reviews-<handle>.json (index) and
        -1.json, -2.json ... (about 40 each, built by build_reviews.py). Nothing is fetched until the tap after the live
        reviews run out. Deduped against the live cards (Loox id, else name + date + first 40 letters of the text),
        rendered in the same card design. Reviews with a photo or video only, never text-only cards.
     3. When every photo review is shown, the next tap loads the official Loox reviews widget inline under the list:
        <div id="looxReviews" data-product-id> with the Loox iframe (id looxReviewsFrame, as Loox names its own, so the
        Loox app script can size it). Height: Loox resize messages from https://loox.io set it; otherwise the iframe
        keeps a tall min-height and scrolls inside. If the iframe has not loaded after ~12 s, the link to the full
        reviews page appears as well.
     Cards with more than one photo get a "1 / 3" button on the photo that steps through them (loads on tap).
     Long texts: clamped to 5 lines; a "Read more" / "Show less" button appears only when the text really overflows.
     Photos: Loox "_mid" size first; on error the original upload; if that fails too, the next photo of the review;
     with no photo left the card becomes a clean text card (no empty media box).
     Video reviews (Loox feed media t = "video", export flag "vids"): the poster shows as the photo. Near the screen a
     muted, looping, inline <video> is added (preload none until then) and plays while half visible, pauses when not.
     Tap = sound + controls. Loox gives only the poster URL, so the video file is tried at the poster's name with .mp4
     (with and without "_orig"); if no file plays, the poster stays as a normal photo (no broken player).
     prefers-reduced-motion: no autoplay, a play button starts it. */
  const STAR_ROW = `<svg viewBox="0 0 88 16" fill="currentColor" aria-hidden="true" focusable="false">${[0, 1, 2, 3, 4].map((i) => `<path transform="translate(${i * 18} 0)" d="M8 .8l2.2 4.6 5 .6-3.7 3.5 1 5L8 12l-4.5 2.5 1-5L.8 6l5-.6z"/>`).join('')}</svg>`;
  const revNorm = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  const revKey = (name, date, text) => `${revNorm(name)}|${String(date || '').slice(0, 10)}|${revNorm(text).slice(0, 40)}`;
  const absUrl = (u) => String(u || '').replace(/^\/\//, 'https://').replace(/^http:/, 'https:');
  const midUrl = (u) => absUrl(u).replace(/(_mid)?\.(jpe?g|png|webp)$/i, '_mid.$2');
  const origUrl = (u) => absUrl(u).replace(/_mid\.(jpe?g|png|webp)$/i, '.$1');
  const fmtDate = (d) => {
    const t = new Date(`${d}T12:00:00Z`);
    return Number.isNaN(t.getTime()) ? '' : t.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
  };
  const PLAY_SVG = '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M8 5v14l11-7z" fill="currentColor"/></svg>';
  const SOUND_SVG = '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M4 9v6h4l5 4V5L8 9H4z" fill="currentColor"/><path d="M16 8.5a5 5 0 0 1 0 7M18.5 6a8.5 8.5 0 0 1 0 12" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>';

  // Photo list of a card's media box (live: data-imgs JSON, else the <img>; export: set by card())
  const mediaImgs = (media) => {
    if (media._jbImgs) return media._jbImgs;
    let imgs = [];
    try { imgs = JSON.parse(media.dataset.imgs || '[]'); } catch (e) { imgs = []; }
    const img = media.querySelector('img');
    if (!imgs.length && img) imgs = [img.dataset.orig || img.getAttribute('src') || ''];
    media._jbImgs = imgs.filter(Boolean);
    media._jbIdx = 0;
    return media._jbImgs;
  };
  const showImg = (media, i) => {
    const img = media.querySelector('img');
    const imgs = mediaImgs(media);
    if (!img || !imgs.length) return;
    media._jbIdx = ((i % imgs.length) + imgs.length) % imgs.length;
    media._jbTriedOrig = false;
    img.dataset.orig = imgs[media._jbIdx];
    img.src = midUrl(imgs[media._jbIdx]);
    if (media._jbStepSet) media._jbStepSet();
  };
  // A card whose media can not be shown: drop the media box, keep the review as a clean text card
  const toTextCard = (media) => {
    const li = media.closest('.jb-pdp-rev__item');
    media.remove();
    if (li) { li.classList.add('jb-pdp-rev__item--text'); li.dataset.kind = 'text'; fitQuote(li); }
  };
  // <img> error: original upload instead of "_mid", then the next photo, then a text card
  const onImgError = (img) => {
    const media = img.closest('.jb-pdp-rev__media');
    if (!media || !media.isConnected) return;
    if (media.classList.contains('is-video') && media._jbVid) return; // the video covers the poster
    const orig = origUrl(img.dataset.orig || img.currentSrc || img.src);
    if (!media._jbTriedOrig && orig && orig !== absUrl(img.currentSrc || img.src)) {
      media._jbTriedOrig = true;
      img.src = orig;
      return;
    }
    const imgs = mediaImgs(media);
    if (imgs.length > 1) {
      imgs.splice(media._jbIdx || 0, 1);
      if (imgs.length < 2 && media._jbStepBtn) { media._jbStepBtn.remove(); media._jbStepBtn = null; media._jbStepSet = null; }
      showImg(media, media._jbIdx || 0);
      return;
    }
    if (media.classList.contains('is-video')) { media._jbNoPoster = true; return; } // the video may still play
    toTextCard(media);
  };
  const watchImg = (img) => {
    if (!img || img._jbW) return;
    img._jbW = true;
    img.addEventListener('error', () => onImgError(img));
    // failed before this script ran (deferred): a finished <img> with no pixels
    if (img.complete && img.getAttribute('src') && !img.naturalWidth) onImgError(img);
  };

  // "1 / 3" photo stepper on a card's photo (live and exported cards)
  function initPhotoStepper(media) {
    if (!media || media._jbStep || media.classList.contains('is-video')) return;
    const imgs = mediaImgs(media);
    if (imgs.length < 2) return;
    media._jbStep = true;
    const img = media.querySelector('img');
    if (!img) return;
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'jb-pdp-rev__next';
    media._jbStepBtn = btn;
    media._jbStepSet = () => {
      const n = mediaImgs(media).length;
      btn.textContent = `${(media._jbIdx || 0) + 1} / ${n} ›`;
      btn.setAttribute('aria-label', `Show next photo (photo ${(media._jbIdx || 0) + 1} of ${n})`);
    };
    btn.addEventListener('click', () => showImg(media, (media._jbIdx || 0) + 1));
    media._jbStepSet();
    media.appendChild(btn);
  }

  // "Read more" only when the clamped text really overflows (measured when the card is visible)
  function fitQuote(li) {
    if (!li || li.hidden) return;
    const q = li.querySelector('.jb-pdp-rev__quote');
    if (!q) return;
    let btn = li.querySelector('.jb-pdp-rev__toggle');
    if (q.classList.contains('is-open')) return;
    const over = q.scrollHeight > q.clientHeight + 2;
    if (!over) { if (btn) btn.hidden = true; return; }
    if (!btn) {
      btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'jb-pdp-rev__toggle';
      if (!q.id) q.id = `jb-rq-${Math.random().toString(36).slice(2, 9)}`;
      btn.setAttribute('aria-controls', q.id);
      btn.setAttribute('aria-expanded', 'false');
      btn.textContent = 'Read more';
      btn.addEventListener('click', () => {
        const open = !q.classList.contains('is-open');
        q.classList.toggle('is-open', open);
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
        btn.textContent = open ? 'Show less' : 'Read more';
        if (!open) {
          const r = li.getBoundingClientRect();
          if (r.top < 0) li.scrollIntoView({ block: 'start', behavior: reduceMotion() ? 'auto' : 'smooth' });
        }
      });
      q.insertAdjacentElement('afterend', btn);
    }
    btn.hidden = false;
  }

  /* Review videos: one shared pair of observers for every video card on the page */
  const revVid = {
    near: null,
    seen: null,
    // Loox gives the poster (".../NAME_mid.jpg" or ".../NAME_orig_mid.jpg"): try NAME.mp4, then without "_orig"
    // Loox review videos live on Cloudflare Stream (uid per Loox post id, read from Loox's quick view, Oct 2026).
    // HLS where the browser plays it natively, else the MP4 download. Unknown posts: poster stays a photo.
    stream: {"2ini-XnKW":"18461dfab2187b8b186a0b9aa8180913","83DW0HaE2C":"22e7c168d27d4476a42e2638fca1f80f","9bDdk-MBF":"e2e0fb6cbddd464c9bf4885863c5a05c","G0HCC5edw":"1d38eaea36b0cd67d6e9161f487027a4","ITsKj-2dW":"c5281ed969f54db0aadb56e15b1f8794","K5WK6oYyg":"15d9fa6ea3f84323a58814cea8d0df8c","M2cF8nnqn":"98be02b55d5d4e5a828c6e0a54862133","PKocLosH2":"909ffd05a0eaa7c4aac6ee780172242b","VJ2trrRL3":"c159d838dc76487099ef483e4972759f","YYhhIHBpg":"f8b6f72b1c6c47109e99463f26ecd402","dX8Xhx1s1":"79281c6307c74226b03d278ff72c7c2e","t6x_1UPR_":"f91bb80e554aba17d0b0345ffe412860","zbeqeo9We":"4cfac93a9ed34a6f8a6e315089bbbd4e"},
    sources(poster) {
      const file = String(poster).split('?')[0].split('/').pop() || '';
      const id = Object.keys(revVid.stream).find((k) => file === k || file.indexOf(k + '_') === 0 || file.indexOf(k + '.') === 0);
      if (!id) return [];
      const uid = revVid.stream[id];
      const hls = document.createElement('video').canPlayType('application/vnd.apple.mpegurl');
      const out = [];
      if (hls) out.push({ src: `https://videodelivery.net/${uid}/manifest/video.m3u8`, type: 'application/vnd.apple.mpegurl' });
      out.push({ src: `https://videodelivery.net/${uid}/downloads/default.mp4`, type: 'video/mp4' });
      return out;
    },
    build(media) {
      if (media._jbVid || media._jbVidFailed) return media._jbVid;
      const img = media.querySelector('img');
      const poster = media.dataset.poster || (img && (img.dataset.orig || img.src)) || '';
      if (!poster) return null;
      const v = document.createElement('video');
      v.className = 'jb-pdp-rev__video';
      v.muted = true;
      v.defaultMuted = true;
      v.loop = true;
      v.playsInline = true;
      v.setAttribute('playsinline', '');
      v.setAttribute('muted', '');
      v.preload = 'metadata';
      v.setAttribute('disablepictureinpicture', '');
      v.setAttribute('controlslist', 'nodownload');
      if (img && img.currentSrc) v.poster = img.currentSrc;
      const who = media.closest('.jb-pdp-rev__item');
      const name = who && who.querySelector('.jb-pdp-rev__who strong');
      v.setAttribute('aria-label', `Customer video from ${name ? name.textContent : 'a customer'}`);
      const srcs = revVid.sources(poster);
      if (!srcs.length) { media._jbVidFailed = true; return null; }
      srcs.forEach((u, k) => {
        const s = document.createElement('source');
        s.src = u.src;
        s.type = u.type;
        if (k === srcs.length - 1) s.addEventListener('error', () => revVid.fail(media));
        v.appendChild(s);
      });
      v.addEventListener('error', () => revVid.fail(media));
      v.addEventListener('playing', () => media.classList.add('is-playing'));
      v.addEventListener('loadeddata', () => media.classList.add('is-ready'));
      media.insertBefore(v, media.firstChild);
      media._jbVid = v;
      return v;
    },
    // No playable file: back to a normal photo tile (or a text card when the poster is broken too)
    fail(media) {
      if (media._jbVidFailed) return;
      media._jbVidFailed = true;
      const v = media._jbVid;
      media._jbVid = null;
      if (v) { if (revVid.seen) revVid.seen.unobserve(v); v.remove(); }
      media.classList.remove('is-video', 'is-playing', 'is-ready');
      media.querySelectorAll('.jb-pdp-rev__vbtn, .jb-pdp-rev__vtag').forEach((b) => b.remove());
      if (media._jbNoPoster) toTextCard(media);
    },
    play(media, user) {
      const v = revVid.build(media);
      if (!v) return;
      const p = v.play();
      if (p && p.catch) p.catch(() => { if (user) media.classList.add('is-paused'); });
    },
    init(media) {
      if (!media || media._jbVidInit) return;
      media._jbVidInit = true;
      const still = reduceMotion();
      const tag = document.createElement('span');
      tag.className = 'jb-pdp-rev__vtag';
      tag.innerHTML = `${PLAY_SVG}<span>Video</span>`;
      tag.setAttribute('aria-hidden', 'true');
      media.appendChild(tag);
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'jb-pdp-rev__vbtn';
      btn.innerHTML = still ? `${PLAY_SVG}<span>Play video</span>` : `${SOUND_SVG}<span>Sound on</span>`;
      btn.addEventListener('click', () => {
        const v = revVid.build(media);
        if (!v) return;
        v.muted = false;
        v.controls = true;
        media._jbUser = true;
        media.classList.add('is-user');
        btn.remove();
        revVid.play(media, true);
      });
      media.appendChild(btn);
      if (still || !('IntersectionObserver' in window)) return;
      if (!revVid.near) {
        revVid.near = new IntersectionObserver((es) => es.forEach((e) => {
          if (!e.isIntersecting) return;
          revVid.near.unobserve(e.target);
          const v = revVid.build(e.target);
          if (v) revVid.seen.observe(v);
        }), { rootMargin: '300px 0px' });
        revVid.seen = new IntersectionObserver((es) => es.forEach((e) => {
          const m = e.target.parentElement;
          if (!m) return;
          if (e.intersectionRatio >= 0.5) { if (!m._jbUser) revVid.play(m); } else if (!e.target.paused) e.target.pause();
        }), { threshold: [0, 0.5] });
      }
      revVid.near.observe(media);
    }
  };

  function initReviews(root) {
    const list = root.querySelector('[data-rev-list]');
    const more = root.querySelector('[data-rev-more]');
    if (root._jb) return;
    root._jb = true;
    const items = list ? Array.from(list.children) : [];
    const status = root.querySelector('[data-rev-status]');
    const allLink = root.querySelector('[data-rev-all]');
    const holder = root.querySelector('[data-loox-holder]');
    const chips = Array.from(root.querySelectorAll('[data-topic]'));
    const base = Number(root.dataset.revBatch) || 6;
    const pageSize = () => (window.matchMedia('(min-width: 1000px)').matches ? base + 2 : base);
    const label = more ? more.textContent : '';
    // topic keywords from the section setting: "color-match:color, colour, match|easy:easy, quick|..."
    const topics = (root.dataset.revTopics || '').split('|').map((t) => {
      const at = t.indexOf(':');
      return at < 1 ? null : { id: t.slice(0, at).trim(), words: t.slice(at + 1).split(',').map((w) => w.trim().toLowerCase()).filter(Boolean) };
    }).filter(Boolean);
    const tagsFor = (text) => {
      const txt = String(text || '').toLowerCase();
      return topics.filter((t) => t.words.some((w) => txt.includes(w))).map((t) => t.id).join(' ');
    };
    // Exported photo reviews (lazy): index URL + chunk URLs from Liquid
    const exportIndex = root.dataset.revExport || '';
    const chunkUrls = (root.dataset.revChunks || '').split(/\s+/).filter(Boolean);
    let exp = exportIndex && list ? { chunks: null, next: 0, done: false, busy: null } : { done: true };
    const seenIds = new Set();
    const seenKeys = new Set();
    const prepCard = (li) => {
      const media = li.querySelector('.jb-pdp-rev__media');
      if (!media) return;
      watchImg(media.querySelector('img'));
      if (media.classList.contains('is-video')) revVid.init(media);
      else initPhotoStepper(media);
    };
    items.forEach((li) => {
      if (li.dataset.revId) seenIds.add(li.dataset.revId);
      const who = li.querySelector('.jb-pdp-rev__who strong');
      const q = li.querySelector('.jb-pdp-rev__quote');
      seenKeys.add(revKey(who && who.textContent, li.dataset.revDate, q && q.textContent));
      prepCard(li);
    });

    let topic = '';
    let limit = pageSize();
    let looxState = ''; // '', 'done', 'failed'
    const matches = () => items.filter((li) => !topic || (` ${li.dataset.topics} `).includes(` ${topic} `));
    const fitShown = () => requestAnimationFrame(() => items.forEach((li) => { if (!li.hidden) fitQuote(li); }));
    const apply = () => {
      const match = matches();
      items.forEach((li) => { li.hidden = true; });
      match.slice(0, limit).forEach((li) => { li.hidden = false; });
      const shown = Math.min(limit, match.length);
      if (status) status.textContent = `Showing ${shown} of ${match.length}${exp.done ? '' : '+'} customer reviews`;
      if (more) more.hidden = !!looxState && shown >= match.length;
      fitShown();
    };
    // Re-measure "Read more" when the width changes (rotation, resize) and once the web font is in
    let lastW = window.innerWidth;
    window.addEventListener('resize', () => { if (window.innerWidth !== lastW) { lastW = window.innerWidth; fitShown(); } }, { passive: true });
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(fitShown).catch(() => {});

    const card = (r) => {
      const li = document.createElement('li');
      li.className = 'jb-pdp-rev__item';
      li.dataset.kind = 'photo';
      li.dataset.source = 'export';
      li.dataset.revId = r.id;
      li.dataset.revDate = r.date || '';
      li.dataset.topics = tagsFor(r.text);
      li.hidden = true;
      const fig = document.createElement('figure');
      const media = document.createElement('div');
      media.className = 'jb-pdp-rev__media';
      const vids = Array.isArray(r.vids) ? r.vids : [];
      const isVid = vids.indexOf(0) >= 0;
      // photos only in the stepper; a video review shows its video (poster first)
      media._jbImgs = isVid ? [r.imgs[0]] : r.imgs.filter((u, k) => vids.indexOf(k) < 0);
      media._jbIdx = 0;
      if (!media._jbImgs.length) media._jbImgs = [r.imgs[0]];
      if (isVid) { media.classList.add('is-video'); media.dataset.kind = 'video'; li.dataset.kind = 'video'; }
      const img = document.createElement('img');
      img.alt = isVid ? `Video from ${r.name}` : `Customer photo from ${r.name}`;
      img.loading = 'lazy';
      img.decoding = 'async';
      img.width = 480;
      img.height = 600;
      img.dataset.orig = media._jbImgs[0];
      img.src = midUrl(media._jbImgs[0]);
      media.appendChild(img);
      fig.appendChild(media);
      const rating = Math.max(0, Math.min(5, Number(r.rating) || 0));
      const stars = document.createElement('span');
      stars.className = 'jb-stars';
      stars.innerHTML = `<span class="jb-stars__bar" role="img"><span style="color:#d9c3cd">${STAR_ROW}</span><span class="jb-stars__fill" style="width: ${rating * 20}%; color: var(--jb-star);">${STAR_ROW}</span></span>`;
      stars.firstChild.setAttribute('aria-label', `Rated ${rating} out of 5 stars`);
      fig.appendChild(stars);
      if (r.variant) {
        const shade = document.createElement('p');
        shade.className = 'jb-pdp-rev__shade';
        shade.textContent = `Shade: ${r.variant}`;
        fig.appendChild(shade);
      }
      if (r.text) {
        const q = document.createElement('blockquote');
        q.className = 'jb-pdp-rev__quote';
        q.textContent = r.text;
        fig.appendChild(q);
      }
      const who = document.createElement('figcaption');
      who.className = 'jb-pdp-rev__who';
      const name = document.createElement('strong');
      name.textContent = r.name;
      who.appendChild(name);
      if (r.verified) { const v = document.createElement('span'); v.textContent = 'Verified buyer'; who.append(' ', v); }
      const ds = r.date ? fmtDate(r.date) : '';
      if (ds) { const d = document.createElement('span'); d.className = 'jb-pdp-rev__date'; d.textContent = ds; who.append(' ', d); }
      fig.appendChild(who);
      li.appendChild(fig);
      return li;
    };

    const addReviews = (reviews) => {
      let added = 0;
      (reviews || []).forEach((r) => {
        if (!r || !r.id || !Array.isArray(r.imgs) || !r.imgs.length) return; // reviews with a photo or video only
        const key = revKey(r.name, r.date, r.text);
        if (seenIds.has(r.id) || seenKeys.has(key)) return;
        seenIds.add(r.id);
        seenKeys.add(key);
        const li = card(r);
        list.appendChild(li);
        items.push(li);
        prepCard(li);
        added += 1;
      });
      // chips with no live reviews become visible once exported reviews match them
      chips.forEach((c) => { if (c.hidden && c.dataset.topic && items.some((li) => (` ${li.dataset.topics} `).includes(` ${c.dataset.topic} `))) c.hidden = false; });
      return added;
    };
    const getJSON = (url) => fetch(url, { credentials: 'same-origin' }).then((res) => { if (!res.ok) throw new Error(`HTTP ${res.status}`); return res.json(); });
    // Load the next export chunk. Resolves to the number of new cards (0 when finished or failed).
    const loadChunk = () => {
      if (exp.done) return Promise.resolve(0);
      if (exp.busy) return exp.busy;
      exp.busy = (async () => {
        try {
          if (!exp.chunks) {
            // fetch the index and chunk 1 together (chunk 1 almost always exists when the index does)
            const first = chunkUrls[0] ? getJSON(chunkUrls[0]).catch(() => null) : Promise.resolve(null);
            const idx = await getJSON(exportIndex);
            const n = Math.min((idx && Array.isArray(idx.chunks) ? idx.chunks.length : 0), chunkUrls.length);
            exp.chunks = chunkUrls.slice(0, n);
            exp.first = n ? first : null;
          }
          if (exp.next >= exp.chunks.length) { exp.done = true; return 0; }
          const i = exp.next;
          exp.next += 1;
          const data = (i === 0 && exp.first ? await exp.first : null) || await getJSON(exp.chunks[i]);
          if (exp.next >= exp.chunks.length) exp.done = true;
          return addReviews(data && data.reviews);
        } catch (e) {
          exp.done = true; // missing or broken export file: go straight to the Loox widget
          return 0;
        } finally {
          exp.busy = null;
        }
      })();
      return exp.busy;
    };

    const showLink = () => { if (allLink) allLink.hidden = false; };
    const fallback = () => {
      looxState = 'failed';
      if (holder) { holder.hidden = true; holder.innerHTML = ''; }
      if (more) more.hidden = true;
      if (allLink) { allLink.hidden = false; const a = allLink.querySelector('a'); if (a) a.focus(); }
    };
    // Loox iframe height from postMessage (format not documented: accept a number, "123", {height}, or JSON of those)
    const readHeight = (d) => {
      if (typeof d === 'string') { try { d = JSON.parse(d); } catch (e) { const m = d.match(/(?:height|h)\D{0,3}(\d{3,6})/i); return m ? Number(m[1]) : Number(d); } }
      if (typeof d === 'number') return d;
      if (d && typeof d === 'object') {
        const v = d.height ?? d.frameHeight ?? d.iframeHeight ?? d.h ?? (d.data && (d.data.height ?? d.data.frameHeight));
        return Number(v);
      }
      return NaN;
    };
    const loadLoox = () => {
      if (looxState) return;
      const frameBase = root.dataset.looxFrame;
      const pid = root.dataset.productId;
      if (!holder || !frameBase || !pid) { fallback(); return; }
      looxState = 'done';
      holder.hidden = false;
      // Only one #looxReviews on the page: reuse (move here) one that exists, else create it
      let box = document.getElementById('looxReviews');
      if (box) { if (!holder.contains(box)) holder.appendChild(box); } else {
        box = document.createElement('div');
        box.id = 'looxReviews';
        box.className = 'loox-reviews-default';
        holder.appendChild(box);
      }
      box.dataset.productId = pid;
      let frame = box.querySelector('iframe');
      if (!frame) {
        frame = document.createElement('iframe');
        frame.id = 'looxReviewsFrame';
        frame.title = 'Customer reviews';
        frame.setAttribute('scrolling', 'auto');
        frame.setAttribute('allowtransparency', 'true');
        frame.src = `${frameBase}?h=${Date.now()}`;
        box.appendChild(frame);
        // If the Loox app script also renders its own iframe into the box, keep Loox's and drop ours
        const mo = new MutationObserver(() => {
          const others = Array.from(box.querySelectorAll('iframe')).filter((f) => f !== frame);
          if (others.length && frame.isConnected) { frame.remove(); mo.disconnect(); }
        });
        mo.observe(box, { childList: true, subtree: true });
        setTimeout(() => mo.disconnect(), 15000);
      }
      const onMsg = (e) => {
        if (!/^https:\/\/([a-z0-9-]+\.)?loox\.io$/.test(e.origin)) return;
        const f = box.querySelector('iframe');
        if (!f || e.source !== f.contentWindow) return;
        const h = readHeight(e.data);
        if (h > 150 && h < 100000) { f.style.height = `${Math.ceil(h)}px`; f.classList.add('is-sized'); }
      };
      window.addEventListener('message', onMsg);
      let loaded = false;
      frame.addEventListener('load', () => { loaded = true; });
      setTimeout(() => { if (!loaded && frame.isConnected) showLink(); }, 12000);
      if (more) more.hidden = true;
      if (status) status.textContent = 'All customer reviews loaded below';
      box.setAttribute('tabindex', '-1');
      box.focus({ preventScroll: true });
    };
    chips.forEach((chip) => chip.addEventListener('click', () => {
      topic = chip.dataset.topic;
      limit = pageSize();
      chips.forEach((c) => c.setAttribute('aria-pressed', c === chip ? 'true' : 'false'));
      apply();
    }));
    if (more) more.addEventListener('click', async () => {
      if (more.getAttribute('aria-busy') === 'true') return;
      const want = limit + pageSize();
      // Pull export chunks until the next batch is full or the export ends. The first fetch happens only once every
      // live card for this topic is shown (no export data before the live reviews run out).
      if (!exp.done && matches().length < want && (exp.chunks || limit >= matches().length)) {
        more.setAttribute('aria-busy', 'true');
        more.textContent = 'Loading more reviews…';
        while (matches().length < want && !exp.done) await loadChunk(); // eslint-disable-line no-await-in-loop
        more.removeAttribute('aria-busy');
        if (label) more.textContent = label;
      }
      const match = matches();
      if (limit >= match.length) { loadLoox(); return; }
      const firstNew = match[limit];
      limit += pageSize();
      apply();
      if (firstNew && !firstNew.hidden) {
        const fig = firstNew.querySelector('figure');
        if (fig) { fig.setAttribute('tabindex', '-1'); fig.focus({ preventScroll: true }); }
      }
      if (label) more.textContent = label;
    });
    apply();
  }

  /* ------------------------------------------------ demo video (jb-pdp-how): muted, loops, plays only in view
     - nothing is downloaded until the video is near the screen (source in data-src, preload="none")
     - plays when at least half of it is visible, pauses when it scrolls away
     - prefers-reduced-motion: no autoplay, a play button starts it with controls */
  function initDemoVideos() {
    const vids = Array.from(document.querySelectorAll('video[data-jb-demo]'));
    if (!vids.length) return;

    // Poster from data-poster once the video is within ~600px: a poster attribute downloads at page load even with preload="none"
    const setPoster = (v) => { if (v.dataset.poster) { v.poster = v.dataset.poster; v.removeAttribute('data-poster'); } };
    const posterIO = 'IntersectionObserver' in window ? new IntersectionObserver((es) => es.forEach((e) => { if (e.isIntersecting) { setPoster(e.target); posterIO.unobserve(e.target); } }), { rootMargin: '600px 0px' }) : null;
    vids.forEach((v) => (posterIO ? posterIO.observe(v) : setPoster(v)));
    const load = (v) => {
      if (v._jbLoaded) return;
      v._jbLoaded = true;
      v.querySelectorAll('source[data-src]').forEach((s) => { s.src = s.dataset.src; s.removeAttribute('data-src'); });
      v.preload = 'auto';
      v.load();
    };
    vids.forEach((v) => {
      v.muted = true;
      const btn = v.parentElement.querySelector('[data-video-play]');
      if (btn) {
        btn.addEventListener('click', () => {
          load(v);
          v._jbUser = true;
          v.controls = true;
          btn.hidden = true;
          v.play().catch(() => { btn.hidden = false; });
        });
      }
      const still = reduceMotion();
      if (btn) btn.hidden = !still;
      if (!('IntersectionObserver' in window)) { if (!still) { load(v); v.play().catch(() => {}); } return; }
      const near = new IntersectionObserver((entries) => {
        entries.forEach((e) => { if (e.isIntersecting) { load(v); near.unobserve(v); } });
      }, { rootMargin: '600px 0px' });
      near.observe(v);
      const seen = new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          if (e.intersectionRatio >= 0.5) {
            if (!reduceMotion() || v._jbUser) { load(v); v.play().catch(() => { if (btn) btn.hidden = false; }); }
          } else if (!v.paused) {
            v.pause();
          }
        });
      }, { threshold: [0, 0.5, 1] });
      seen.observe(v);
    });
  }

  const boot = () => {
    document.querySelectorAll('.jb-pdp-rev').forEach(initReviews);
    initDemoVideos();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
})();
